import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode, useCallback } from 'react';
import { doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { onIdTokenChanged } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { hasVerifiedTotp } from '../auth/authState';
import { useYearContext } from './YearContext';
import { SyncRunResult, runSheetSync, SyncTabKey } from '../firebase/sheetSync';

interface SheetSyncValue {
    /**
     * Increments every time a sync run completes. Domain contexts include this
     * in their fetch dependencies, so a change that arrives from the spreadsheet
     * refreshes every open screen.
     */
    dataVersion: number;
    lastRun: Date | null;
    lastResult: SyncRunResult | null;
    isSyncing: boolean;
    syncNow: (tabs?: SyncTabKey[]) => Promise<SyncRunResult>;
}

const SheetSyncContext = createContext<SheetSyncValue | undefined>(undefined);

/**
 * Bridges Google-Sheets sync back into the UI.
 *
 * The domain contexts fetch on demand rather than subscribing to Firestore, so
 * a row edited in the spreadsheet would otherwise sit in Firestore unseen until
 * the admin changed year or reloaded. Rather than convert six contexts to live
 * subscriptions, we watch the ONE document each sync run stamps — the year's
 * config doc — and turn its `lastRun` into a refresh signal. One listener,
 * and both the scheduled poll and a manual "Sync now" refresh the whole app.
 */
export const SheetSyncProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { selectedYear } = useYearContext();
    const [dataVersion, setDataVersion] = useState(0);
    const [lastRun, setLastRun] = useState<Date | null>(null);
    const [lastResult, setLastResult] = useState<SyncRunResult | null>(null);
    const [isSyncing, setIsSyncing] = useState(false);

    useEffect(() => {
        setLastRun(null);
        setLastResult(null);

        let unsubscribeSnapshot: (() => void) | undefined;
        let authGeneration = 0;
        let cancelled = false;
        const unsubscribeAuth = onIdTokenChanged(auth, user => {
            const generation = ++authGeneration;
            unsubscribeSnapshot?.();
            unsubscribeSnapshot = undefined;
            if (!user) return;

            void (async () => {
                // Firebase reports a user as signed in before the TOTP endpoint
                // refreshes the custom claim. Avoid opening a protected listener
                // during that transition or for a logged-in non-admin user.
                if (cancelled) return;
                const verified = await hasVerifiedTotp(user);
                if (cancelled || !verified || generation !== authGeneration) return;

                const ref = doc(db, 'samadhi_sheet_sync', String(selectedYear));
                const unsubscribe = onSnapshot(
                    ref,
                    snapshot => {
                        if (cancelled || generation !== authGeneration) return;
                        const data = snapshot.data();
                        if (!data) return;

                        const runAt = data.lastRun instanceof Timestamp ? data.lastRun.toDate() : null;
                        setLastResult((data.lastResult as SyncRunResult) ?? null);

                        setLastRun(previous => {
                            // Only a *new* run means new data. Bumping on every snapshot
                            // would refetch on unrelated config edits.
                            if (runAt && (!previous || runAt.getTime() !== previous.getTime())) {
                                setDataVersion(v => v + 1);
                            }
                            return runAt;
                        });
                    },
                    // A missing doc or a rules denial is normal for a year with sync off.
                    () => undefined
                );
                if (cancelled || generation !== authGeneration) {
                    unsubscribe();
                    return;
                }
                unsubscribeSnapshot = unsubscribe;
            })();
        });

        return () => {
            cancelled = true;
            authGeneration++;
            unsubscribeSnapshot?.();
            unsubscribeSnapshot = undefined;
            unsubscribeAuth();
        };
    }, [selectedYear]);

    const syncNow = useCallback(
        async (tabs?: SyncTabKey[]) => {
            setIsSyncing(true);
            try {
                const { result } = await runSheetSync(selectedYear, tabs);
                // The snapshot listener will also fire, but bump immediately so
                // the caller sees fresh data without waiting for the round trip.
                setDataVersion(v => v + 1);
                return result;
            } finally {
                setIsSyncing(false);
            }
        },
        [selectedYear]
    );

    const value = useMemo(
        () => ({ dataVersion, lastRun, lastResult, isSyncing, syncNow }),
        [dataVersion, lastRun, lastResult, isSyncing, syncNow]
    );

    return <SheetSyncContext.Provider value={value}>{children}</SheetSyncContext.Provider>;
};

export const useSheetSync = (): SheetSyncValue => {
    const ctx = useContext(SheetSyncContext);
    if (!ctx) throw new Error('useSheetSync must be used within a SheetSyncProvider');
    return ctx;
};

/**
 * The refresh signal on its own.
 *
 * Returns 0 when there is no provider, so domain contexts can consume it
 * unconditionally and still render in tests that mount them bare.
 */
export const useSyncSignal = (): number => useContext(SheetSyncContext)?.dataVersion ?? 0;
